import { useState } from 'react'
import type { ReactElement } from 'react'
import { useAutoRefresh } from '../lib/use-auto-refresh.ts'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import {
  acknowledgeAlert,
  getDashboard,
  resolveAlert,
  runAlertChecks,
} from '../lib/alerts-api.ts'
import {
  ActiveSessionsPanel,
  HarnessPanel,
  IdListPanel,
  OpenAlertsPanel,
} from '../components/DashboardPanels.tsx'
import { Button, Callout, PageHeader } from '../components/ui/index.ts'

export const Route = createFileRoute('/dashboard')({
  loader: async () =>
    withSessionRedirect(async () => {
      const dashboard = await getDashboard()
      return { dashboard }
    }),
  component: DashboardRoute,
})

/** Harness status flips within the 45s offline threshold and active
 * sessions change constantly, so this wants to be reasonably live — but
 * it aggregates several queries per load, so not as eager as a session
 * list. */
const REFRESH_MS = 10_000

function DashboardRoute(): ReactElement {
  const { dashboard } = Route.useLoaderData()
  useAutoRefresh(REFRESH_MS)
  const router = useRouter()
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    await router.invalidate()
  }

  async function handleRunChecks() {
    setRunning(true)
    setError(null)
    try {
      await runAlertChecks()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRunning(false)
    }
  }

  async function handleAck(id: string) {
    setError(null)
    try {
      await acknowledgeAlert(id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleResolve(id: string) {
    setError(null)
    try {
      await resolveAlert(id)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Dashboard"
        description="Harness liveness, what is running, and anything waiting on a human."
        actions={
          // `isLoading` rather than swapping the label to "Running…": the
          // button keeps its width, so the control an operator is about to
          // click next does not move under the cursor mid-request.
          <Button
            variant="primary"
            isLoading={running}
            onClick={() => void handleRunChecks()}
          >
            Run checks now
          </Button>
        }
      />

      {error !== null && (
        <div className="mb-2">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}

      <HarnessPanel harnesses={dashboard.harnesses} />
      <ActiveSessionsPanel sessions={dashboard.sessions.active} />
      <OpenAlertsPanel
        alerts={dashboard.alerts.open}
        onAcknowledge={(id) => void handleAck(id)}
        onResolve={(id) => void handleResolve(id)}
      />
      <IdListPanel
        title="Stale sessions"
        caption="Sessions whose harness has stopped reporting them"
        heading="Session id"
        ids={dashboard.staleSessions}
        emptyHeadline="No stale sessions"
        emptyDescription="Every session the database knows about is still being reported by its harness."
      />
      <IdListPanel
        title="Pending approvals"
        caption="Skill versions waiting on a human to publish them"
        heading="Approval id"
        ids={dashboard.pendingApprovals}
        emptyHeadline="Nothing awaiting approval"
        emptyDescription="Publishing a skill is a deliberate act; anything queued for one shows up here."
      />
    </div>
  )
}
